/**
 * Redux slice for the Audit Use Case Configuration pages.
 *
 * Follows the same pattern as auditQueueSlice.ts (createAsyncThunk + extraReducers).
 *
 * All thunks (get/create/update) call the real backend via auditModuleService.
 */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { LOADING_STATE } from "src/types/constants";
import AuditModuleService from "src/services/auditModuleService";
import { UseCaseConfig } from "src/components/pages/auditModule/shared/types";

// ----------------------------  Types  ----------------------------

export interface AuditConfigState {
  useCaseConfigs: UseCaseConfig[];
  getConfigsLoading: LOADING_STATE;
  saveConfigLoading: LOADING_STATE;
}

const initialState: AuditConfigState = {
  useCaseConfigs: [],
  getConfigsLoading: "idle",
  saveConfigLoading: "idle",
};

// ----------------------------  Async Thunks  ----------------------------

export const getUseCaseConfigs = createAsyncThunk("auditConfig/getConfigs", async () => {
  const response = await AuditModuleService.getUseCaseConfigs();
  return response.configs;
});

export const createUseCaseConfig = createAsyncThunk(
  "auditConfig/createConfig",
  async (config: UseCaseConfig) => {
    const response = await AuditModuleService.createUseCaseConfig(config);
    return response.config;
  },
);

export const updateUseCaseConfig = createAsyncThunk(
  "auditConfig/updateConfig",
  async (config: UseCaseConfig) => {
    const response = await AuditModuleService.updateUseCaseConfig(config);
    return response.config;
  },
);

// ----------------------------  Slice  ----------------------------

const { reducer, actions } = createSlice({
  name: "auditConfigSlice",
  initialState,
  reducers: {
    resetSaveStatus: (state) => {
      state.saveConfigLoading = "idle";
    },
    resetGetConfigsStatus: (state) => {
      state.getConfigsLoading = "idle";
    },
  },
  extraReducers: (builder) => {
    // Get configs
    builder.addCase(getUseCaseConfigs.pending, (state) => {
      state.getConfigsLoading = "pending";
    });
    builder.addCase(getUseCaseConfigs.fulfilled, (state, { payload }) => {
      state.useCaseConfigs = payload;
      state.getConfigsLoading = "fulfilled";
    });
    builder.addCase(getUseCaseConfigs.rejected, (state) => {
      state.getConfigsLoading = "rejected";
    });

    // Create config
    builder.addCase(createUseCaseConfig.pending, (state) => {
      state.saveConfigLoading = "pending";
    });
    builder.addCase(createUseCaseConfig.fulfilled, (state, { payload }) => {
      state.useCaseConfigs.push(payload);
      state.saveConfigLoading = "fulfilled";
    });
    builder.addCase(createUseCaseConfig.rejected, (state) => {
      state.saveConfigLoading = "rejected";
    });

    // Update config
    builder.addCase(updateUseCaseConfig.pending, (state) => {
      state.saveConfigLoading = "pending";
    });
    builder.addCase(updateUseCaseConfig.fulfilled, (state, { payload }) => {
      const idx = state.useCaseConfigs.findIndex((c) => c.id === payload.id);
      if (idx !== -1) {
        state.useCaseConfigs[idx] = payload;
      }
      state.saveConfigLoading = "fulfilled";
    });
    builder.addCase(updateUseCaseConfig.rejected, (state) => {
      state.saveConfigLoading = "rejected";
    });
  },
});

export const { resetSaveStatus, resetGetConfigsStatus } = actions;

export default reducer;
